import { API_CONFIG } from '../config/api';
import type {
  Product,
  CreateProductData,
  UpdateProductData,
  ProductImage
  // Removed ProductsResponse and ProductResponse
} from '../types/product';

// Get all products (public)
export const getPublicProducts = async (): Promise<Product[]> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/public/products`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch public products:', response.status, errorText);
      throw new Error(`Failed to fetch public products: ${response.status}`);
    }

    const result = await response.json();
    
    // Debug: Log the raw API response
    console.log('🔍 Raw API response:', result);
    console.log('🔍 result.data:', result.data);
    console.log('🔍 First product in result.data:', result.data?.[0]);
    console.log('🔍 First product main_image_url:', result.data?.[0]?.main_image_url);
    console.log('🔍 First product image_url:', result.data?.[0]?.image_url);
    console.log('🔍 First product image:', result.data?.[0]?.image);
    console.log('🔍 First product images:', result.data?.[0]?.images);

    // Handle different response formats
    if (result.data && Array.isArray(result.data)) {
      console.log('🔍 Returning result.data');
      return result.data;
    } else if (Array.isArray(result)) {
      console.log('🔍 Returning result directly');
      return result;
    }

    console.log('🔍 Returning empty array');
    return [];
  } catch (error) {
    console.error('Error fetching public products:', error);
    throw new Error('Failed to fetch public products');
  }
};

// Get product by ID (public)
export const getPublicProductById = async (id: number): Promise<Product> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/public/products/${id}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch public product:', response.status, errorText);
      throw new Error(`Failed to fetch public product: ${response.status}`);
    }

    const result = await response.json();

    // Handle different response formats
    if (result.data) {
      return result.data;
    }

    return result;
  } catch (error) {
    console.error('Error fetching public product:', error);
    throw new Error('Failed to fetch public product');
  }
};

// Get products by category (public)
export const getPublicProductsByCategory = async (categoryId: number): Promise<Product[]> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/public/products/category/${categoryId}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch products by category:', response.status, errorText);
      throw new Error(`Failed to fetch products by category: ${response.status}`);
    }

    const result = await response.json();

    // Handle different response formats
    if (result.data && Array.isArray(result.data)) {
      return result.data;
    } else if (Array.isArray(result)) {
      return result;
    }

    return [];
  } catch (error) {
    console.error('Error fetching products by category:', error);
    throw new Error('Failed to fetch products by category');
  }
};

// Get all products
export const getAllProducts = async (): Promise<Product[]> => {
  try {
    // Try the protected endpoint first, fallback to public if it fails
    const token = localStorage.getItem('token');

    if (token) {
      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/products`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const result = await response.json();

          // Handle different response formats
          if (result.data && Array.isArray(result.data)) {
            return result.data;
          } else if (Array.isArray(result)) {
            return result;
          }

          return [];
        }
      } catch (protectedError) {
        console.warn('Protected endpoint failed, trying public endpoint:', protectedError);
      }
    }

    // Fallback to public endpoint
    const response = await fetch(`${API_CONFIG.BASE_URL}/public/products`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch products (public):', response.status, errorText);
      throw new Error(`Failed to fetch products: ${response.status}`);
    }

    const result = await response.json();

    // Handle different response formats
    if (result.data && Array.isArray(result.data)) {
      return result.data;
    } else if (Array.isArray(result)) {
      return result;
    }

    return [];
  } catch (error) {
    console.error('Error fetching products:', error);
    throw new Error('Failed to fetch products');
  }
};

// Get product by ID
export const getProductById = async (id: number): Promise<Product> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/products/${id}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch product:', response.status, errorText);
      throw new Error(`Failed to fetch product: ${response.status}`);
    }

    const result = await response.json();

    // Handle different response formats
    if (result.data) {
      return result.data;
    }

    return result;
  } catch (error) {
    console.error('Error fetching product:', error);
    throw new Error('Failed to fetch product');
  }
};

// Create a new product with multiple images
export const createProduct = async (productData: CreateProductData): Promise<Product> => {
  try {
    // Convert to FormData as the API expects form data with file upload
    const formData = new FormData();

    // Add product fields - ensure all values are strings
    formData.append('code', String(productData.code || '').trim());
    formData.append('name', String(productData.name || ''));
    formData.append('description', String(productData.description || ''));
    formData.append('category_id', String(productData.category_id));
    formData.append('brand_id', String(productData.brand_id));
    formData.append('base_unit_id', String(productData.base_unit_id));
    formData.append('price', String(parseFloat(productData.price.toString()).toFixed(2)));
    formData.append('type', String(productData.type));
    formData.append('has_installment', String(productData.has_installment));
    formData.append('status', String(productData.status));

    // Add multiple images if provided
    if (productData.images && productData.images.length > 0) {
      productData.images.forEach((image) => {
        formData.append('images[]', image);
      });

      // Set primary image index (first image is primary by default)
      const primaryIndex = productData.primary_image_index !== undefined ? productData.primary_image_index : 0;
      formData.append('primary_image_index', primaryIndex.toString());
    }

    console.log('Creating product with data:', productData);

    const response = await fetch(`${API_CONFIG.BASE_URL}/products`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        // Note: Don't set Content-Type for FormData, let browser set it
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Product creation failed:', response.status, errorText);

      let errorMessage = 'Failed to create product';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        // If response is not JSON, use the text
        errorMessage = errorText || errorMessage;
      }

      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Product created successfully:', result);

    // Handle different response formats
    if (result.data) {
      return result.data;
    }

    return result;
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
};

// Update a product with multiple images
export const updateProduct = async (productData: UpdateProductData): Promise<Product> => {
  try {
    // Check if we have images to upload
    const hasImages = productData.images && productData.images.length > 0;

    let response: Response;

    if (hasImages) {
      // Use FormData for image uploads
      const formData = new FormData();

      // Add product fields - ensure all values are strings
      formData.append('code', String(productData.code || '').trim());
      formData.append('name', String(productData.name || ''));
      formData.append('description', String(productData.description || ''));
      formData.append('category_id', String(productData.category_id));
      formData.append('brand_id', String(productData.brand_id));
      formData.append('base_unit_id', String(productData.base_unit_id));
      formData.append('price', String(parseFloat(productData.price.toString()).toFixed(2)));
      formData.append('type', String(productData.type));
      formData.append('has_installment', String(productData.has_installment));
      formData.append('status', String(productData.status));

      // Add new images
      productData.images!.forEach((image) => {
        formData.append('images[]', image);
      });

      // Set primary image index
      if (productData.primary_image_index !== undefined) {
        formData.append('primary_image_index', productData.primary_image_index.toString());
      }

      // Set whether to replace all existing images
      if (productData.replace_all_images !== undefined) {
        formData.append('replace_all_images', productData.replace_all_images.toString());
      }

      // Add _method override for Laravel
      formData.append('_method', 'PUT');

      console.log('Updating product with FormData (has images):', productData);
      console.log('FormData contents:');
      for (const [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }

      response = await fetch(`${API_CONFIG.BASE_URL}/products/${productData.id}`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });
    } else {
      // Use JSON for text-only updates
      const jsonData = {
        code: (productData.code || '').trim(),
        name: productData.name || '',
        description: productData.description || '',
        category_id: productData.category_id,
        brand_id: productData.brand_id,
        base_unit_id: productData.base_unit_id,
        price: parseFloat(productData.price.toString()),
        type: productData.type,
        has_installment: productData.has_installment,
        status: productData.status,
      };

      console.log('Updating product with JSON (no images):', jsonData);
      console.log('has_installment in JSON:', jsonData.has_installment, 'type:', typeof jsonData.has_installment);

      response = await fetch(`${API_CONFIG.BASE_URL}/products/${productData.id}`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(jsonData),
      });
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Product update failed:', response.status, errorText);
      console.error('Response headers:', response.headers);
      console.error('Request URL:', response.url);

      let errorMessage = 'Failed to update product';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
        console.error('Parsed error data:', errorData);
      } catch {
        // If response is not JSON, use the text
        errorMessage = errorText || errorMessage;
        console.error('Raw error text:', errorText);
      }

      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Product updated successfully:', result);

    // Handle different response formats
    if (result.data) {
      return result.data;
    }

    return result;
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

// Upload multiple images to existing product
export const uploadMultipleImages = async (productId: number, images: File[], primaryImageIndex: number = 0): Promise<Product> => {
  try {
    const formData = new FormData();

    // Add images
    images.forEach((image) => {
      formData.append('images[]', image);
    });

    // Set primary image index
    formData.append('primary_image_index', primaryImageIndex.toString());

    const response = await fetch(`${API_CONFIG.BASE_URL}/products/${productId}/upload-multiple-images`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Image upload failed:', response.status, errorText);

      let errorMessage = 'Failed to upload images';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }

      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Images uploaded successfully:', result);

    if (result.data) {
      return result.data;
    }

    return result;
  } catch (error) {
    console.error('Error uploading images:', error);
    throw error;
  }
};

// Delete a specific product image
export const deleteProductImage = async (productId: number, imageId: number): Promise<void> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/products/${productId}/images/${imageId}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Image deletion failed:', response.status, errorText);

      let errorMessage = 'Failed to delete image';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }

      throw new Error(errorMessage);
    }

    console.log('Image deleted successfully');
  } catch (error) {
    console.error('Error deleting image:', error);
    throw error;
  }
};

// Delete a product
export const deleteProduct = async (id: number): Promise<void> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/products/${id}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Product deletion failed:', response.status, errorText);

      let errorMessage = 'Failed to delete product';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }

      throw new Error(errorMessage);
    }

    console.log('Product deleted successfully');
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

// Get product images by product ID
export const getProductImages = async (productId: number): Promise<ProductImage[]> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/products/${productId}/images`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch product images:', response.status, errorText);
      throw new Error(`Failed to fetch product images: ${response.status}`);
    }

    const result = await response.json();

    if (result.data && Array.isArray(result.data)) {
      return result.data;
    }

    return [];
  } catch (error) {
    console.error('Error fetching product images:', error);
    throw error;
  }
};

// Add unit to product
export const addProductUnit = async (productId: number, unitId: number, conversionRate: number): Promise<void> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/products/${productId}/units`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({
        unit_id: unitId,
        conversion_rate: conversionRate
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to add product unit:', response.status, errorText);

      let errorMessage = 'Failed to add unit to product';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }

      throw new Error(errorMessage);
    }

    console.log('Product unit added successfully');
  } catch (error) {
    console.error('Error adding product unit:', error);
    throw error;
  }
};

// Delete unit from product
export const deleteProductUnit = async (productId: number, unitId: number): Promise<void> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/products/${productId}/units/${unitId}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to delete product unit:', response.status, errorText);

      let errorMessage = 'Failed to delete unit from product';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }

      throw new Error(errorMessage);
    }

    console.log('Product unit deleted successfully');
  } catch (error) {
    console.error('Error deleting product unit:', error);
    throw error;
  }
};

// Helper function to generate unique product code
export const generateProductCode = (): string => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `PRD-${timestamp}-${random}`;
};